'use client';

import Link from 'next/link';
import { useState, Suspense } from 'react';
import Logo from '@/components/Logo';
import { useRouter, useSearchParams } from 'next/navigation';
import { csrfHeaders } from '@/lib/csrf';

function LoginForm() {
  const router  = useRouter();
  const params  = useSearchParams();
  const from    = params.get('from') ?? '/dashboard';

  const [email,      setEmail]      = useState('');
  const [password,   setPassword]   = useState('');
  const [codigo2FA,  setCodigo2FA]  = useState('');
  const [requiere2FA, setRequiere2FA] = useState(false);
  const [usarBackupCode, setUsarBackupCode] = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res  = await fetch('/api/auth/login', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body:    JSON.stringify({ email, password, codigo2FA: requiere2FA ? codigo2FA : undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.requiere2FA) { setRequiere2FA(true); setError('Código incorrecto. Intentá nuevamente.'); return; }
        setError(data.error ?? 'Credenciales incorrectas');
        return;
      }
      if (data.requiere2FA) { setRequiere2FA(true); return; }
      window.location.href = from;
    } catch {
      setError('No se pudo conectar con el servidor');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative w-full max-w-sm">
      {/* Logo */}
      <div className="flex flex-col items-center mb-10 gap-3">
        <Logo height={64} href="/" className="brightness-[1.8]" />
        <p className="text-xs text-white/25">Facturación Electrónica · ARCA</p>
      </div>

      {/* Card */}
      <div className="bg-mrcode-surface border border-white/8 rounded-2xl p-8">
        <h1 className="text-xl font-bold text-white mb-1">Iniciar sesión</h1>
        <p className="text-sm text-white/40 mb-7">Ingresá a tu cuenta de facturación</p>

        {error && (
          <div className="mb-5 flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-red-400 text-sm">
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {requiere2FA ? (
            <div>
              <p className="text-sm text-white/60 mb-4">
                {usarBackupCode ? 'Ingresá uno de tus códigos de recuperación (formato XXXX-XXXX).' : 'Ingresá el código de 6 dígitos de tu app de autenticación.'}
              </p>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                {usarBackupCode ? 'Código de recuperación' : 'Código de verificación'}
              </label>
              <input
                type="text"
                inputMode={usarBackupCode ? 'text' : 'numeric'}
                maxLength={usarBackupCode ? 9 : 6}
                value={codigo2FA}
                onChange={e => setCodigo2FA(usarBackupCode ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                placeholder={usarBackupCode ? 'XXXX-XXXX' : '000000'}
                className="w-full bg-white/6 border border-white/10 rounded-lg px-4 py-2.5 text-white text-center text-lg tracking-[0.3em] placeholder-white/25 focus:outline-none focus:border-mrcode-gold/60 transition-colors"
              />
              <div className="flex items-center justify-between mt-3">
                <button
                  type="button"
                  onClick={() => { setRequiere2FA(false); setCodigo2FA(''); setError(null); }}
                  className="text-xs text-white/30 hover:text-white/60 transition-colors"
                >
                  ← Volver
                </button>
                <button
                  type="button"
                  onClick={() => { setUsarBackupCode(!usarBackupCode); setCodigo2FA(''); setError(null); }}
                  className="text-xs text-white/30 hover:text-white/60 transition-colors"
                >
                  {usarBackupCode ? 'Usar código de la app' : '¿Perdiste el acceso? Usar código de recuperación'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="tu@empresa.com"
                  className="w-full bg-white/6 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/25 focus:outline-none focus:border-mrcode-gold/60 transition-colors text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Contraseña</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full bg-white/6 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/25 focus:outline-none focus:border-mrcode-gold/60 transition-colors text-sm"
                />
              </div>

              <div className="flex justify-end">
                <Link href="/forgot-password" className="text-xs text-white/30 hover:text-mrcode-gold transition-colors">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-mrcode-gold text-black font-bold text-sm hover:bg-mrcode-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            )}
            {loading ? 'Verificando...' : requiere2FA ? 'Verificar código' : 'Ingresar'}
          </button>
        </form>
      </div>

      <p className="text-center text-sm text-white/30 mt-6">
        ¿No tenés cuenta?{' '}
        <Link href="/register" className="text-mrcode-gold hover:underline font-medium">
          Registrarse gratis
        </Link>
      </p>
    </div>
  );
}

export default function LoginPage() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      {/* Botón volver al home */}
      <div className="absolute top-5 left-5">
        <Link
          href="/"
          className="flex items-center gap-2 text-sm text-white/40 hover:text-white/70 transition-colors group"
        >
          <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver al inicio
        </Link>
      </div>

      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-96 h-96 bg-mrcode-gold/5 rounded-full blur-3xl pointer-events-none" />
      <Suspense>
        <LoginForm />
      </Suspense>
    </div>
  );
}
